import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineX, HiOutlineTrash } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";
import FilePreviewModal, { detectPreviewKind } from "components/FilePreviewModal";
import type { FilePreviewKind } from "components/FilePreviewModal";
import ConfirmDialog from "components/ConfirmDialog";

import {
  LEGAL_CERTIFICATE_ADDRESSEE_DESIGNATIONS,
  LEGAL_CERTIFICATE_APPLICANT_TYPES,
  LEGAL_CERTIFICATE_CLASS_NUMBERS,
  LEGAL_CERTIFICATE_FORM_OF_ADDRESS_OPTIONS,
  LEGAL_CERTIFICATE_PLACE_OPTIONS,
  LEGAL_CERTIFICATE_TYPE_OF_MARK_OPTIONS,
  deleteLegalCertificateDocument,
  generateLegalCertificateUploadPresignedUrls,
  getLegalCertificateDiscussionDetails,
  registerLegalCertificateDocuments,
  submitLegalCertificateApplicationDetails,
  type LegalCertificateApplicationInput,
} from "../../api/legalCertificate";
import { getLeadPrefill } from "../../api/assignment";

export const LEGAL_CERTIFICATE_APPLICATION_DETAILS_FORM_ID =
  "legal-certificate-application-details-form";

const ACCEPTED_TYPES = ".jpg,.jpeg,.png,.webp,.pdf";
const ACCEPTED_HELP = "JPEG, PNG, PDF formats, up to 50MB";
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const FILE_INPUT_ID = "legal-certificate-application-details-file-input";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const REQ = <span className="text-red-500 ml-0.5">*</span>;
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "w-full min-h-[64px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border resize-y";

type SavedDocument = {
  documentId: string;
  fileKey: string;
  fileName: string;
  fileUrl: string | null;
};

type EditableApplication = {
  rowId: string;
  tmNo: string;
  dateOfFiling: Date | null;
  mark: string;
  typeOfMark: string;
  classNumber: string;
  validityDate: Date | null;
  userDate: Date | null;
};

const MAX_APPLICATIONS = 50;

let rowIdCounter = 0;
function makeRowId(): string {
  rowIdCounter += 1;
  return `lc-app-row-${rowIdCounter}-${Date.now()}`;
}

function makeEmptyApplication(defaultMark: string = ""): EditableApplication {
  return {
    rowId: makeRowId(),
    tmNo: "",
    dateOfFiling: null,
    mark: defaultMark,
    typeOfMark: "",
    classNumber: "",
    validityDate: null,
    userDate: null,
  };
}

function parseDateInput(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODateString(date: Date | null): string | null {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function LegalCertificateApplicationDetailsFromClientView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);

  // Addressee
  const [designation, setDesignation] = useState("");
  const [designationCustomName, setDesignationCustomName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [place, setPlace] = useState("");
  const [placeManualMode, setPlaceManualMode] = useState(false);
  const [formOfAddress, setFormOfAddress] = useState("");

  // Applicant
  const [applicantName, setApplicantName] = useState("");
  const [applicantAddress, setApplicantAddress] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [signatoryName, setSignatoryName] = useState("");
  const [signatoryDesignation, setSignatoryDesignation] = useState("");

  // TM applications — count is driven by `numberOfApplications`.
  const [applications, setApplications] = useState<EditableApplication[]>([
    makeEmptyApplication(),
  ]);
  const [preliminaryMark, setPreliminaryMark] = useState<string>("");
  const [pendingDeleteRowId, setPendingDeleteRowId] = useState<string | null>(null);

  // Documents
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Inline validation
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const hasError = (key: string) => errors.has(key);
  const errorBorder = (key: string) => (hasError(key) ? " !border-red-500" : "");
  const clearError = (key: string) => {
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  const [preview, setPreview] = useState<{
    url: string;
    name: string;
    kind: FilePreviewKind;
    revokeOnClose?: boolean;
  } | null>(null);

  const prefilledRef = useRef(false);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getLegalCertificateDiscussionDetails(leadId)
      .then(async (details) => {
        if (cancelled) return;

        const prelimMark = details.preliminary?.mark ?? "";
        setPreliminaryMark(prelimMark);

        const d = details.applicationDetails;
        if (d) {
          const designations =
            LEGAL_CERTIFICATE_ADDRESSEE_DESIGNATIONS as readonly string[];
          const desigValue = (d.designation ?? "").trim();
          if (desigValue && !designations.includes(desigValue)) {
            setDesignation("NAME");
            setDesignationCustomName(desigValue);
          } else {
            setDesignation(desigValue);
            setDesignationCustomName("");
          }
          setFirmName(d.firmName ?? "");
          setPlace(d.place ?? "");
          setFormOfAddress(d.formOfAddress ?? "");
          setApplicantName(d.applicantName ?? "");
          setApplicantAddress(d.applicantAddress ?? "");
          setApplicantType(d.applicantType ?? "");
          setSignatoryName(d.signatoryName ?? "");
          setSignatoryDesignation(d.signatoryDesignation ?? "");
        }

        const savedApps = d?.applications ?? [];
        const count =
          d?.numberOfApplications != null && d.numberOfApplications > 0
            ? d.numberOfApplications
            : Math.max(savedApps.length, 1);

        const rows: EditableApplication[] = Array.from({ length: count }, (_, i) => {
          const app = savedApps[i];
          if (!app) return makeEmptyApplication(prelimMark);
          return {
            rowId: makeRowId(),
            tmNo: app.tmNo ?? "",
            dateOfFiling: parseDateInput(app.dateOfFiling),
            mark: prelimMark || app.mark || "",
            typeOfMark: app.typeOfMark ?? "",
            classNumber: app.classNumber != null ? String(app.classNumber) : "",
            validityDate: parseDateInput(app.validityDate),
            userDate: parseDateInput(app.userDate),
          };
        });
        setApplications(rows);

        setSavedDocuments(
          (details.documents ?? []).map((doc) => ({
            documentId: doc.documentId,
            fileKey: doc.fileKey,
            fileName: doc.filename,
            fileUrl: doc.downloadUrl ?? null,
          }))
        );

        // Prefill from reference lead when nothing has been saved yet.
        const hasSavedAppDetails = Boolean(
          d && (d.applicantName || d.applicantAddress)
        );
        const hasSavedApplications = savedApps.length > 0;
        if (
          !prefilledRef.current &&
          details.referenceLeadId &&
          !hasSavedAppDetails
        ) {
          prefilledRef.current = true;
          try {
            const prefill = await getLeadPrefill(details.referenceLeadId);
            if (cancelled) return;
            if (prefill.applicant) setApplicantName(prefill.applicant);
            if (prefill.applicantAddress)
              setApplicantAddress(prefill.applicantAddress);
            if (prefill.applicantType) setApplicantType(prefill.applicantType);
            if (prefill.signatoryName) setSignatoryName(prefill.signatoryName);
            if (prefill.signatoryDesignation)
              setSignatoryDesignation(prefill.signatoryDesignation);
            if (prefill.addressee?.formOfAddress)
              setFormOfAddress(prefill.addressee.formOfAddress);
            if (prefill.addressee?.districtOfApplicant)
              setPlace(prefill.addressee.districtOfApplicant);
            const line1 = prefill.addressee?.addresseeLine1?.trim();
            if (line1) {
              const designations =
                LEGAL_CERTIFICATE_ADDRESSEE_DESIGNATIONS as readonly string[];
              if (designations.includes(line1) && line1 !== "NAME") {
                setDesignation(line1);
              } else {
                setDesignation("NAME");
                setDesignationCustomName(line1);
              }
            }
            const line2 = prefill.addressee?.addresseeLine2?.trim();
            if (line2) setFirmName(line2);
            if (
              !hasSavedApplications &&
              Array.isArray(prefill.applications) &&
              prefill.applications.length > 0
            ) {
              setApplications(
                prefill.applications.map<EditableApplication>((a) => ({
                  rowId: makeRowId(),
                  tmNo: a.tmNo ?? "",
                  dateOfFiling: parseDateInput(a.applDate),
                  mark: prelimMark || a.mark || "",
                  typeOfMark: a.typeOfMark ?? "",
                  classNumber:
                    a.classNumber != null ? String(a.classNumber) : "",
                  validityDate: null,
                  userDate: parseDateInput(a.userDate?.date),
                }))
              );
            }
          } catch {
            // prefill is best-effort; ignore errors
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load application details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const addApplicationRow = () => {
    setApplications((prev) => {
      if (prev.length >= MAX_APPLICATIONS) {
        toast.error(`You can add up to ${MAX_APPLICATIONS} applications`);
        return prev;
      }
      return [...prev, makeEmptyApplication(preliminaryMark)];
    });
  };

  const removeApplicationRow = (rowId: string) => {
    setApplications((prev) => {
      const next = prev.filter((row) => row.rowId !== rowId);
      return next.length === 0 ? [makeEmptyApplication(preliminaryMark)] : next;
    });
  };

  const updateApplication = (
    rowId: string,
    patch: Partial<EditableApplication>
  ) => {
    setApplications((prev) =>
      prev.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row))
    );
    for (const field of Object.keys(patch)) {
      clearError(`app:${rowId}:${field}`);
    }
  };

  const onFilesSelected = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    const accepted: File[] = [];
    for (const file of arr) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} exceeds the 50MB limit`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length === 0) return;
    setPendingFiles((prev) => [...prev, ...accepted]);
  };

  const removePendingFile = (idx: number) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeSavedDocument = async (idx: number) => {
    const doc = savedDocuments[idx];
    if (!doc || !leadId) return;
    try {
      await deleteLegalCertificateDocument(leadId, doc.documentId);
      setSavedDocuments((prev) => prev.filter((_, i) => i !== idx));
      toast.success("Document removed");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to remove document"
      );
    }
  };

  const openPendingPreview = (file: File) => {
    const url = URL.createObjectURL(file);
    setPreview({
      url,
      name: file.name,
      kind: detectPreviewKind(file.name, file.type),
      revokeOnClose: true,
    });
  };

  const openSavedPreview = (doc: SavedDocument) => {
    if (!doc.fileUrl) {
      toast.error("Preview is not available for this file");
      return;
    }
    setPreview({
      url: doc.fileUrl,
      name: doc.fileName,
      kind: detectPreviewKind(doc.fileName),
    });
  };

  const closePreview = () => {
    if (preview?.revokeOnClose) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const totalFileCount = pendingFiles.length + savedDocuments.length;
  const showDesignationCustomName = designation === "NAME";

  const designationOptions = useMemo(
    () =>
      (LEGAL_CERTIFICATE_ADDRESSEE_DESIGNATIONS as readonly string[]).map(
        (d) => ({
          value: d,
          label: d === "NAME" ? "Name (Other)" : d,
        })
      ),
    []
  );

  const placeIsManual =
    placeManualMode ||
    (place !== "" &&
      !(LEGAL_CERTIFICATE_PLACE_OPTIONS as readonly string[]).includes(place));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const next = new Set<string>();
    if (!designation) next.add("designation");
    if (designation === "NAME" && !designationCustomName.trim())
      next.add("designationCustomName");
    if (!firmName.trim()) next.add("firmName");
    if (!place.trim()) next.add("place");
    if (!formOfAddress) next.add("formOfAddress");
    if (!applicantName.trim()) next.add("applicantName");
    if (!applicantAddress.trim()) next.add("applicantAddress");
    if (!applicantType) next.add("applicantType");
    if (!signatoryName.trim()) next.add("signatoryName");
    if (!signatoryDesignation.trim()) next.add("signatoryDesignation");

    for (const row of applications) {
      if (!row.tmNo.trim()) next.add(`app:${row.rowId}:tmNo`);
      if (!row.dateOfFiling) next.add(`app:${row.rowId}:dateOfFiling`);
      if (!row.typeOfMark) next.add(`app:${row.rowId}:typeOfMark`);
      if (!row.classNumber) next.add(`app:${row.rowId}:classNumber`);
      if (!row.validityDate) next.add(`app:${row.rowId}:validityDate`);
    }

    if (next.size > 0) {
      setErrors(next);
      return;
    }
    setErrors(new Set());

    setSaving(true);
    try {
      // Upload any pending files first.
      if (pendingFiles.length > 0) {
        const presigned = await generateLegalCertificateUploadPresignedUrls(
          leadId,
          pendingFiles.map((file) => ({
            filename: file.name,
            mimeType: file.type || "application/octet-stream",
          }))
        );
        if (presigned.length !== pendingFiles.length) {
          throw new Error("Failed to generate upload URLs");
        }
        await Promise.all(
          pendingFiles.map(async (file, i) => {
            const target = presigned[i];
            const putRes = await fetch(target.url, {
              method: "PUT",
              body: file,
              headers: {
                "Content-Type": file.type || "application/octet-stream",
              },
            });
            if (!putRes.ok) throw new Error(`Failed to upload ${file.name}`);
          })
        );
        const registeredDocs = pendingFiles.map((file, i) => ({
          fileKey: presigned[i].key,
          filename: file.name,
        }));
        await registerLegalCertificateDocuments(leadId, registeredDocs);
      }

      const designationValue =
        designation === "NAME" ? designationCustomName.trim() : designation;

      const applicationItems: LegalCertificateApplicationInput[] =
        applications.map((row) => ({
          tmNo: row.tmNo.trim(),
          dateOfFiling: toISODateString(row.dateOfFiling),
          mark: preliminaryMark || null,
          typeOfMark: row.typeOfMark || null,
          classNumber:
            row.classNumber && !isNaN(Number(row.classNumber))
              ? Number(row.classNumber)
              : null,
          validityDate: toISODateString(row.validityDate),
          userDate: toISODateString(row.userDate),
        }));

      await submitLegalCertificateApplicationDetails(leadId, {
        numberOfApplications: applications.length,
        designation: designationValue || undefined,
        firmName: firmName.trim() || undefined,
        place: place.trim() || undefined,
        formOfAddress: formOfAddress || undefined,
        applicantName: applicantName.trim() || undefined,
        applicantAddress: applicantAddress.trim() || undefined,
        applicantType: applicantType || undefined,
        signatoryName: signatoryName.trim() || undefined,
        signatoryDesignation: signatoryDesignation.trim() || undefined,
        applications: applicationItems,
      });

      if (pendingFiles.length > 0) {
        setPendingFiles([]);
      }

      toast.success("Application details saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to save application details"
      );
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_APPLICATION_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* --- Addressee --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Addressee</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Designation{REQ}</label>
            <Select
              value={designation}
              onValueChange={(v) => {
                setDesignation(v);
                clearError("designation");
              }}
            >
              <SelectTrigger
                className={INPUT_CLASS + errorBorder("designation")}
              >
                <SelectValue placeholder="Select Designation" />
              </SelectTrigger>
              <SelectContent>
                {designationOptions.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showDesignationCustomName && (
            <div className="flex flex-col gap-1">
              <label className={LABEL_CLASS}>Name{REQ}</label>
              <input
                type="text"
                value={designationCustomName}
                onChange={(e) => {
                  setDesignationCustomName(e.target.value);
                  clearError("designationCustomName");
                }}
                placeholder="Enter name"
                className={INPUT_CLASS + errorBorder("designationCustomName")}
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Form of Address{REQ}</label>
            <Select
              value={formOfAddress}
              onValueChange={(v) => {
                setFormOfAddress(v);
                clearError("formOfAddress");
              }}
            >
              <SelectTrigger
                className={INPUT_CLASS + errorBorder("formOfAddress")}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {LEGAL_CERTIFICATE_FORM_OF_ADDRESS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Firm Name{REQ}</label>
            <input
              type="text"
              value={firmName}
              onChange={(e) => {
                setFirmName(e.target.value);
                clearError("firmName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("firmName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className={LABEL_CLASS}>Place{REQ}</label>
              {placeIsManual && (
                <button
                  type="button"
                  onClick={() => {
                    setPlaceManualMode(false);
                    setPlace("");
                    clearError("place");
                  }}
                  className="text-[11px] font-normal text-[#0070F0] hover:underline"
                >
                  Choose from list
                </button>
              )}
            </div>
            {placeIsManual ? (
              <input
                type="text"
                value={place}
                onChange={(e) => {
                  setPlace(e.target.value);
                  clearError("place");
                }}
                placeholder="Enter place"
                className={INPUT_CLASS + errorBorder("place")}
                autoFocus={placeManualMode && !place}
              />
            ) : (
              <Select
                value={place}
                onValueChange={(v) => {
                  if (v === "__OTHER__") {
                    setPlaceManualMode(true);
                    setPlace("");
                    clearError("place");
                    return;
                  }
                  setPlace(v);
                  clearError("place");
                }}
              >
                <SelectTrigger className={INPUT_CLASS + errorBorder("place")}>
                  <SelectValue placeholder="Select Place" />
                </SelectTrigger>
                <SelectContent>
                  {LEGAL_CERTIFICATE_PLACE_OPTIONS.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                  <SelectItem value="__OTHER__">Other (Enter manually)</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* --- Applicant Details --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">
          Applicant Details
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Name{REQ}</label>
            <input
              type="text"
              value={applicantName}
              onChange={(e) => {
                setApplicantName(e.target.value);
                clearError("applicantName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("applicantName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Applicant Type{REQ}</label>
            <Select
              value={applicantType}
              onValueChange={(v) => {
                setApplicantType(v);
                clearError("applicantType");
              }}
            >
              <SelectTrigger
                className={INPUT_CLASS + errorBorder("applicantType")}
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {LEGAL_CERTIFICATE_APPLICANT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1 md:col-span-2">
            <label className={LABEL_CLASS}>Applicant Address{REQ}</label>
            <textarea
              value={applicantAddress}
              onChange={(e) => {
                setApplicantAddress(e.target.value);
                clearError("applicantAddress");
              }}
              placeholder="Address"
              className={TEXTAREA_CLASS + errorBorder("applicantAddress")}
              rows={2}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory's Name{REQ}</label>
            <input
              type="text"
              value={signatoryName}
              onChange={(e) => {
                setSignatoryName(e.target.value);
                clearError("signatoryName");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("signatoryName")}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Signatory's Designation{REQ}</label>
            <input
              type="text"
              value={signatoryDesignation}
              onChange={(e) => {
                setSignatoryDesignation(e.target.value);
                clearError("signatoryDesignation");
              }}
              placeholder="Text Here"
              className={INPUT_CLASS + errorBorder("signatoryDesignation")}
            />
          </div>
        </div>
      </div>

      {/* --- Supporting Documents (lead-level) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            Supporting Documents
          </p>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              if (saving) return;
              document.getElementById(FILE_INPUT_ID)?.click();
            }}
            className="h-9 px-3 inline-flex items-center justify-center rounded-[8px] bg-white border border-[#D5D3E0] text-[12px] font-medium text-[#2E3037] hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.02)" }}
          >
            Select Files
          </button>
        </div>
        <p className="text-[11px] text-[#6D6D6D] -mt-3">{ACCEPTED_HELP}</p>

        <input
          id={FILE_INPUT_ID}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) {
              onFilesSelected(e.target.files);
            }
            e.currentTarget.value = "";
          }}
        />

        {totalFileCount > 0 && (
          <div className="flex flex-col gap-2 self-stretch">
            {savedDocuments.map((doc, dIdx) => (
              <div
                key={`saved-${doc.documentId}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-[#FCFCFC]"
              >
                <span className="text-xs text-[#222222] truncate min-w-0 flex-1">
                  {doc.fileName}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openSavedPreview(doc)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  {doc.fileUrl ? (
                    <a
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={doc.fileName}
                      className="px-3 h-8 inline-flex items-center justify-center rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                    >
                      Download
                    </a>
                  ) : null}
                  <button
                    type="button"
                    aria-label={`Remove ${doc.fileName}`}
                    onClick={() => removeSavedDocument(dIdx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
            {pendingFiles.map((file, pIdx) => (
              <div
                key={`pending-${pIdx}-${file.name}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-[8px] border border-[#EEE] bg-white"
              >
                <span className="text-xs text-[#16A34A] truncate min-w-0 flex-1">
                  {file.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => openPendingPreview(file)}
                    className="px-3 h-8 rounded-[8px] border border-[#D5D3E0] bg-white text-xs font-medium text-[#2E3037] hover:bg-gray-50"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => removePendingFile(pIdx)}
                    disabled={saving}
                    className="text-[#6D6D6D] hover:text-red-600 p-1 disabled:opacity-50"
                  >
                    <HiOutlineX className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- TM Applications (grid-table layout) --- */}
      <div className="flex flex-col gap-5 self-stretch bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">TM Applications</p>
          <button
            type="button"
            onClick={addApplicationRow}
            disabled={applications.length >= MAX_APPLICATIONS}
            className="text-sm font-normal text-[#0070F0] hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            + Add Application
          </button>
        </div>

        <div>
          <div className="flex flex-col">
            <div className="grid grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)_120px_140px_140px_28px] gap-3 px-1 pb-2">
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Application No.{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Date of Filing{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Type of Mark{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Class Number{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">Validity Date{REQ}</div>
              <div className="text-[12px] font-normal leading-5 text-[#6D6D6D]">User Date</div>
              <div />
            </div>

            {applications.map((row) => (
              <div
                key={row.rowId}
                className="flex flex-col gap-2 py-3 border-t border-[#EEEEEE] first:border-t-0"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_140px_minmax(0,1fr)_120px_140px_140px_28px] gap-3 px-1 items-start">
                  <input
                    type="text"
                    value={row.tmNo}
                    onChange={(e) => updateApplication(row.rowId, { tmNo: e.target.value })}
                    placeholder="e.g. 6120578"
                    className={INPUT_CLASS + errorBorder(`app:${row.rowId}:tmNo`)}
                  />
                  <DateTimePicker
                    value={row.dateOfFiling}
                    onChange={(date) => updateApplication(row.rowId, { dateOfFiling: date })}
                    placeholder="Select Date"
                    buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:dateOfFiling`)}
                  />
                  <Select
                    value={row.typeOfMark}
                    onValueChange={(v) => updateApplication(row.rowId, { typeOfMark: v })}
                  >
                    <SelectTrigger className={INPUT_CLASS + errorBorder(`app:${row.rowId}:typeOfMark`)}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEGAL_CERTIFICATE_TYPE_OF_MARK_OPTIONS.map((d) => (
                        <SelectItem key={d} value={d}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={row.classNumber}
                    onValueChange={(v) => updateApplication(row.rowId, { classNumber: v })}
                  >
                    <SelectTrigger className={INPUT_CLASS + errorBorder(`app:${row.rowId}:classNumber`)}>
                      <SelectValue placeholder="Select Class" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEGAL_CERTIFICATE_CLASS_NUMBERS.map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <DateTimePicker
                    value={row.validityDate}
                    onChange={(date) => updateApplication(row.rowId, { validityDate: date })}
                    placeholder="Select Date"
                    buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:validityDate`)}
                  />
                  <DateTimePicker
                    value={row.userDate}
                    onChange={(date) => updateApplication(row.rowId, { userDate: date })}
                    placeholder="Select Date"
                    buttonClassName={"px-[10px] py-2 rounded-[6px] text-[12px]" + errorBorder(`app:${row.rowId}:userDate`)}
                  />
                  <div className="flex items-center justify-center h-9">
                    {applications.length > 1 ? (
                      <button
                        type="button"
                        aria-label="Remove application"
                        onClick={() => setPendingDeleteRowId(row.rowId)}
                        className="text-[#6D6D6D] hover:text-red-600 p-1"
                      >
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FilePreviewModal
        open={!!preview}
        onClose={closePreview}
        name={preview?.name ?? ""}
        url={preview?.url}
        kind={preview?.kind}
      />

      <ConfirmDialog
        open={!!pendingDeleteRowId}
        title="Delete Application"
        description="Are you sure you want to delete this application? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => {
          if (pendingDeleteRowId) removeApplicationRow(pendingDeleteRowId);
          setPendingDeleteRowId(null);
        }}
        onClose={() => setPendingDeleteRowId(null)}
      />
    </form>
  );
}
